<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import LoadingSpinner from '@/components/partials/Loading/LoadingSpinner.vue'
import FilterAccordion from '@/components/partials/FilterAccordion.vue'
import AddressButton from '@/pages/establishment/components/redesign/EstablishmentToolbarTop/AddressButton.vue'
import DistributionToggle from '@/components/Buttons/DistributionToggle.vue'
import { Distribution } from '@/enums/Distribution'
import { useSessionStore } from '@/stores/useSessionStore'
import { useMarketplaceStore } from '@/stores/ssr/useMarketplaceStore'
import { useI18n } from "vue-i18n"

const { t } = useI18n()
const marketplaceStore = useMarketplaceStore()
const sessionStore = useSessionStore()

const emit = defineEmits([ 'openLocationModal' ])

const isLoadingCategories = computed(() => marketplaceStore.categories === null)

const route = useRoute()

const showSortingFilters = computed<boolean>(() => String(route.name).endsWith('marketplace'))

const showAddressButtonDistributionToggle = computed<boolean>(() => String(route.name).endsWith('marketplace'))

const activeFiltersAmount = computed(() => {
  const amount = marketplaceStore.filteredCategories?.length
  return amount > 9 ? '+' : amount
})

const distribution = computed({
  get: () => sessionStore.distribution,
  set: async val => sessionStore.setDistribution(val),
})

function openLocationModal() {
  emit('openLocationModal')
}

async function toggleDistributionType(event: MouseEvent, type: Distribution) {
  if (type === Distribution.Delivery && sessionStore.location === null) {
    openLocationModal()

    return
  }

  distribution.value = type
}
</script>

<template>
  <div id="sidebar"
       class="sidebar"
       data-test-id="sidebar">
    <div class="sidebar__buttons" v-if="showAddressButtonDistributionToggle">
      <AddressButton data-test-id="sidebar-address"
                     @open-modal="openLocationModal"
                     variant="grey"
                     size="lg"/>
      <DistributionToggle data-test-id="distribution-toggle-sidebar"
                          variant="light"
                          v-model="distribution"
                          @toggle="toggleDistributionType"
                          size="lg"/>
    </div>
    <div class="sidebar__accordions">
      <FilterAccordion filter-type="sorting" data-test-id="accordion-sorting-sidebar" v-if="showSortingFilters">
        {{ t('terms.sorting') }}
      </FilterAccordion>

      <FilterAccordion filter-type="categories" data-test-id="accordion-categories-sidebar">
        <div class="sidebar__title">
          {{ t('categories.categories') }}
          <div class="sidebar__notification" v-if="activeFiltersAmount">
            {{ activeFiltersAmount }}
          </div>
        </div>
        <LoadingSpinner v-if="isLoadingCategories"/>
      </FilterAccordion>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.sidebar {
  position: relative;
  display:  none;
  transition: padding 0.2s;

  @include lg-up {
    @include bistroo-scrollbar;
    position:   sticky;
    display:    flex;
    top:        0;
    width:      18rem;
    height:     max-content;
    padding:    0 1rem 2rem 0;
    flex-direction: column;
    flex:       0 0 auto;
    gap:        3rem;
    overflow-x: auto;
    overflow-y: auto;
    max-height: calc(100vh - 14rem);
  }

  &__title {
    position: relative;
  }

  &__notification {
    display:       flex;
    align-items:   center;
    justify-content: center;
    width:         1rem;
    height:        1rem;
    border-radius: 50%;
    position:      absolute;
    top:           50%;
    right:         -1.5rem;
    transform:     translateY(-50%);
    @include gradient-button;
    font-size:     0.7rem;
    color:         #ffffff;
    line-height:   100%;
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap:     1rem;

    &:deep(.address-button) {
      width: 100%;
      max-width: 100%;
    }
  }

  &__accordions {
    display: flex;
    gap:     3rem;
    flex-direction: column;
  }

  &:deep(.accordion) {
    margin: 0;
  }
}
</style>
